import React, { useEffect } from "react";
import { Button, Label } from "semantic-ui-react";
import useLogin from "@common/components/hooks/useLogin";

// Actions, hooks and helpers
import IdentityFormModalAction from "@common/redux/actions/IdentityFormModalAction";
import useCustomerForm from "../hooks/useCustomerForm";
import { searchCustomer } from "@common/components/helpers/Customer/searchCustomer";

// Components
import SearchableDropdownComponent from "@common/components/shared/forms/SearchableDropdownComponent";
import CheckboxComponent from "@common/components/shared/forms/CheckboxComponent";
import TableComponent from "@common/components/shared/forms/TableComponent";
import fetchIdentityOptions from "../helpers/Customer/fetchIdentityOptions";
import { useSelector } from "react-redux";

const CustomerForm = ({
  onSelect,
  handleNewCustomer,
  handleExists,
  handleEditCustomer,
  handleRemoveCustomer,
  handleResetCustomer,
  showCustomerUnknownCheckbox = true,
  label = "Search Customer",
  placeholder = "Search for a customer...",
  unknownNameLabel = "Customer unknown",
  showContacts = false,
  selectContainerStyle = "w-100",
}) => {
  const {
    customers,
    loadingCustomers,
    hasPhoneOrEmail,
    formType,
    customersOptions,
    methods,
    customerUnknown,
    handleAddition,
    addUnknownCustomer,
    editCustomer,
    additionButton,
    hideCustomerSearch,
    isValidPhoto,
  } = useCustomerForm({
    handleExists,
    handleNewCustomer,
    handleEditCustomer,
    handleResetCustomer,
    showContacts,
    showCustomerUnknownCheckbox,
  });

  const { identityFormOptions } = useSelector((store) =>
    IdentityFormModalAction.getStatePath(store),
  );
  const showEmail =
    identityFormOptions?.customerConfiguration?.hasEmailAddress > 0;
  const showPhone =
    identityFormOptions?.customerConfiguration?.hasPhoneNumber > 0;

  const requireEmail =
    identityFormOptions?.customerConfiguration?.hasEmailAddress === 2;
  const requirePhone =
    identityFormOptions?.customerConfiguration?.hasPhoneNumber === 2;

  const {
    setValue,
    control,
    formState: { errors },
  } = methods;

  const [{ isInReadMode }] = useLogin();

  useEffect(fetchIdentityOptions, []);

  return (
    <>
      {!hideCustomerSearch && (
        <SearchableDropdownComponent
          name="searchedCustomer"
          label={label}
          placeholder={placeholder}
          control={control}
          error={errors.searchedCustomer}
          options={customersOptions}
          loading={loadingCustomers}
          allowAdditions={true}
          additionButton={additionButton}
          handleAddition={handleAddition}
          handleSearch={(searchQuery) =>
            searchCustomer(searchQuery, customers, false)
          }
          handleFocus={() => {
            searchCustomer("", customers, false);
            setValue("customerUnknown", false);
          }}
          onType={IdentityFormModalAction.onType}
          noResultsMessage="No results found"
          onSelect={onSelect}
          selectContainerStyle={selectContainerStyle}
        />
      )}
      {showCustomerUnknownCheckbox && !isInReadMode && !hideCustomerSearch && (
        <div className="flex-1">
          <CheckboxComponent
            control={control}
            name="customerUnknown"
            label={unknownNameLabel}
          />
          {customerUnknown && (
            <Button
              className="mb-1"
              type="button"
              onClick={addUnknownCustomer}
              positive
            >
              Add Customer
            </Button>
          )}
        </div>
      )}
      {customers && customers.length > 0 && (
        <>
          <TableComponent
            rows={customers}
            handleRemove={handleRemoveCustomer}
            handleEdit={editCustomer}
            showContacts={showContacts && (showPhone || showEmail)}
          />
          {formType === "unresolvedDispute" &&
            showContacts &&
            (requirePhone || requireEmail) &&
            !hasPhoneOrEmail && (
              <Label basic color="red">
                Please edit the customer to add phone or email
              </Label>
            )}
          {!isValidPhoto && (
            <Label basic color="red" className="mb-1">
              Please edit the customer to add photo
            </Label>
          )}
        </>
      )}
    </>
  );
};

export default CustomerForm;
